"use client";

import { createElement, memo, useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { useWindowSize } from "usehooks-ts";

import { SidebarToggle } from "@/components/sidebar-toggle";
import { Button } from "@/components/ui/button";
import { PlusIcon, VercelIcon } from "./icons";
import { useSidebar } from "./ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { type VisibilityType, VisibilitySelector } from "./visibility-selector";
import {
  Telescope,
  TrendingUp,
  AlertTriangle,
  BookOpen,
  X,
  Download,
  PanelRightClose,
  PanelRightOpen,
  Eye,
  EyeOff,
  Mail,
  ChevronRight,
} from "lucide-react";
import type { UIMessage } from "ai";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { NotifyExplainerModal } from "@/components/notify-explainer-modal";
import {
  SHOW_SERVICE_DISRUPTION_BANNER,
  SHOW_HANDBOOK_BANNER,
  SHOW_DOWNLOAD_BANNER,
} from "@/lib/feature-flags";
import { useArtifact } from "@/hooks/use-artifact";
import PlansModal from "@/components/plans-modal";
import {
  CHANGELOG_DOT_VERSION,
  CHANGELOG_SEEN_PREFIX,
} from "@/lib/announcements";

function PureChatHeader({
  chatId,
  selectedVisibilityType,
  isReadonly,
  section,
  title,
  subtitle,
  showSidebarToggle = true,
  hasMessages = false,
  messages = [],
  append,
  isStreaming = false,
  viewMode,
  overlayVariant = "default",
}: {
  chatId: string;
  selectedVisibilityType: VisibilityType;
  isReadonly: boolean;
  section?: "chat";
  title?: string;
  subtitle?: string;
  showSidebarToggle?: boolean;
  hasMessages?: boolean;
  messages?: UIMessage[];
  append?: (message: any) => void;
  isStreaming?: boolean;
  viewMode?: string;
  /** Home hero: frosted bar over the dither background instead of solid fill. */
  overlayVariant?: "default" | "hero";
}) {
  const router = useRouter();
  const pathname = usePathname();
  // On /alerts/[id] the artifact IS the whole page — toggling it off drops
  // the user into a useless plain chat shell (the <Artifact> unmounts and
  // <Chat> falls back to its non-artifact branch). We hide the PanelRight
  // toggle there so users can't accidentally break their own view. The
  // binary canvas ↔ chat toggle on /alerts/[id] is handled by the
  // left/right peek tabs in <Artifact> instead.
  const isAlertsRoute = pathname?.startsWith("/alerts/") ?? false;
  const {} = useSidebar();
  const { user } = useAuth();
  const { artifact, setArtifact } = useArtifact();
  const [isHandbookBannerDismissed, setIsHandbookBannerDismissed] =
    useState(false);
  const [isDownloadBannerDismissed, setIsDownloadBannerDismissed] =
    useState(false);
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);
  const [hideFlash, setHideFlash] = useState(false);
  const [notifyRegistered, setNotifyRegistered] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyExplainerOpen, setNotifyExplainerOpen] = useState(false);
  const [notifyModalDismissed, setNotifyModalDismissed] = useState<boolean | null>(null);
  const wasStreamingRef = useRef(isStreaming);
  const [showChangelogDot, setShowChangelogDot] = useState(false);

  const persistNotifyModalDismissal = async (dontShowAgain: boolean) => {
    if (!dontShowAgain || notifyModalDismissed) return;
    try {
      await apiClient.dismissNotifyModal();
      setNotifyModalDismissed(true);
    } catch {
      toast.error("Couldn't save your preference.");
    }
  };

  const enableNotifyForChat = async () => {
    if (!user?.email) {
      toast.error("No email found on your account.");
      return;
    }
    setNotifyLoading(true);
    try {
      await apiClient.registerNotifyOnComplete(chatId, user.email);
      setNotifyRegistered(true);
      toast.success("You'll be emailed after the next run in this chat finishes.");
    } catch {
      toast.error("Failed to update notification.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const shouldShowNotifyExplainer = async () => {
    if (notifyModalDismissed !== null) return !notifyModalDismissed;
    try {
      const { should_show } = await apiClient.getNotifyModalStatus();
      setNotifyModalDismissed(!should_show);
      return should_show;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!chatId || !user || isReadonly) {
      setNotifyRegistered(false);
      return;
    }
    let cancelled = false;
    apiClient.getNotifyOnComplete(chatId).then((res) => {
      if (!cancelled) setNotifyRegistered(res.registered);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [chatId, isReadonly, user]);

  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    wasStreamingRef.current = isStreaming;

    if (!chatId || !user || isReadonly || isStreaming || !wasStreaming) return;

    let cancelled = false;
    apiClient.getNotifyOnComplete(chatId).then((res) => {
      if (!cancelled) setNotifyRegistered(res.registered);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [chatId, isReadonly, isStreaming, user]);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("handbook_banner_dismissed");
    if (dismissed === "true") {
      setIsHandbookBannerDismissed(true);
    }
    const downloadDismissed = sessionStorage.getItem(
      "download_banner_dismissed"
    );
    if (downloadDismissed === "true") {
      setIsDownloadBannerDismissed(true);
    }

    // Changelog dot: show if the user hasn't seen this version yet.
    try {
      const seen = window.localStorage.getItem(
        `${CHANGELOG_SEEN_PREFIX}${CHANGELOG_DOT_VERSION}`
      );
      setShowChangelogDot(!seen);
    } catch {
      setShowChangelogDot(true);
    }
  }, []);

  const { width: windowWidth } = useWindowSize();
  const isMobileViewport = typeof windowWidth === "number" && windowWidth < 768;
  const isAlertMode = viewMode === "alert" || viewMode === "signal";
  const artifactToggleLabel = isAlertMode
    ? artifact.isVisible && !artifact.isArtifactMinimized
      ? "Full Chat"
      : "Back to Split"
    : artifact.isVisible
      ? "Back to Chat"
      : "View Full Analysis";

  const sectionConfig = {
    chat: { icon: Telescope, label: "Research", href: "/" },
  };

  return (
    <>
      <header
        className={cn(
          "flex sticky top-0 z-30 items-center gap-2 py-0.5 pl-3 pr-4",
          overlayVariant === "hero"
            ? "border-b border-white/[0.08] bg-black/15 backdrop-blur-xl"
            : !user
              ? "border-b border-border bg-background"
              : "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        )}
      >
        {user && showSidebarToggle && (
          <>
            <SidebarToggle />
            <div className="h-4 w-px bg-border/50" />
          </>
        )}

        {!user && (
          <Link
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="flex aspect-square w-9 h-9 items-center justify-center rounded-lg overflow-hidden">
              <Image
                src="/logo.png"
                alt="Xynth Logo"
                width={36}
                height={36}
                className="w-full h-full object-contain"
              />
            </div>
          </Link>
        )}

        {/* Breadcrumb Navigation - only show when logged in */}
        {user && section && title && (
          <span className="text-white font-medium overflow-x-auto whitespace-nowrap scrollbar-hide min-w-0 max-w-[60vw] md:max-w-none flex items-center gap-2">
            {title === "Alert mode" && viewMode === "signal" ? (
              <span className="inline-flex items-center rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-400">
                Signal mode
              </span>
            ) : title === "Alert mode" ? (
              <span className="inline-flex items-center rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-xs font-semibold text-sky-400">
                Alert mode
              </span>
            ) : (
              title
            )}
          </span>
        )}
        {user && section && !title && (
          <Link
            href={sectionConfig[section].href}
            className="flex items-center gap-1.5 text-white font-medium transition-colors hover:text-white"
          >
            {createElement(sectionConfig[section].icon, { size: 16 })}
            <span className="whitespace-nowrap">
              {sectionConfig[section].label}
            </span>
          </Link>
        )}
        {user && section && title && subtitle && (
          <>
            <ChevronRight className="size-3.5 text-white/70 shrink-0" />
            <span className="text-white/80">{subtitle}</span>
          </>
        )}

        {/* Artifact Toggle Buttons - visible to all viewers with messages */}
        {messages.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label={
                    artifact.hideSteps
                      ? "Show every step inline"
                      : "Use released-only step view"
                  }
                  className={`h-8 gap-0 ${artifact.isVisible ? '' : 'md:gap-2'} rounded-md border border-border/50 bg-background/40 px-2 ${artifact.isVisible ? '' : 'md:px-3'} text-xs font-medium text-foreground/85 backdrop-blur-sm hover:bg-muted/50 hover:text-foreground transition-all duration-200 ${hideFlash ? 'scale-95 brightness-125 ring-1 ring-foreground/20' : ''}`}
                  onClick={() => {
                    setHideFlash(true);
                    setTimeout(() => setHideFlash(false), 300);
                    setArtifact((prev) => ({
                      ...prev,
                      hideSteps: !prev.hideSteps,
                    }));
                  }}
                >
                  {artifact.hideSteps ? (
                    <Eye className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 shrink-0" />
                  )}
                  {!artifact.isVisible && (
                  <span className="hidden md:inline">
                    {artifact.hideSteps ? "Unhide Steps" : "Hide Steps"}
                  </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {artifact.hideSteps
                  ? "Show every step inline"
                  : "Use released-only step view"}
              </TooltipContent>
            </Tooltip>

            {/* Email notification - only for authenticated chat owners */}
            {user && !isReadonly && (
            <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={notifyRegistered ? "Turn off email notification" : "Turn on email notification"}
                    disabled={notifyLoading}
                    className="group flex h-8 items-center gap-2 rounded-md border border-border/50 bg-background/40 px-2 md:px-3 backdrop-blur-sm hover:bg-muted/50 transition-all duration-200 disabled:opacity-50"
                    onClick={async () => {
                      if (notifyRegistered) {
                        setNotifyLoading(true);
                        try {
                          await apiClient.cancelNotifyOnComplete(chatId);
                          setNotifyRegistered(false);
                          toast.success("Notification cancelled.");
                        } catch {
                          toast.error("Failed to update notification.");
                        } finally {
                          setNotifyLoading(false);
                        }
                        return;
                      }

                      if (!user?.email) {
                        toast.error("No email found on your account.");
                        return;
                      }

                      const showExplainer = await shouldShowNotifyExplainer();
                      if (showExplainer) {
                        setNotifyExplainerOpen(true);
                        return;
                      }

                      await enableNotifyForChat();
                    }}
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
                    {!artifact.isVisible && (
                    <span className="hidden md:inline text-xs font-medium text-foreground/80">Email</span>
                    )}
                    <div
                      className={`relative h-[18px] w-[32px] rounded-full transition-colors duration-200 ${
                        notifyRegistered ? "bg-emerald-500" : "bg-red-500/80"
                      }`}
                    >
                      <div
                        className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          notifyRegistered ? "translate-x-[16px]" : "translate-x-[2px]"
                        }`}
                      />
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {notifyRegistered
                    ? "Email notification on — click to turn off"
                    : "Email notification off — click to turn on"}
                </TooltipContent>
              </Tooltip>
            )}

            {!isAlertsRoute && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-label={artifactToggleLabel}
                    className={`h-8 gap-0 ${artifact.isVisible ? '' : 'md:gap-2'} rounded-md border border-border/50 bg-background/40 px-2 ${artifact.isVisible ? '' : 'md:px-3'} text-xs font-medium text-foreground/85 backdrop-blur-sm hover:bg-muted/50 hover:text-foreground`}
                    onClick={() =>
                      setArtifact((prev) => {
                        if (isAlertMode) {
                          if (!prev.isVisible) {
                            return {
                              ...prev,
                              isVisible: true,
                              isChatMinimized: isMobileViewport,
                              isArtifactMinimized: false,
                            };
                          }

                          if (isMobileViewport) {
                            const showCanvas = prev.isArtifactMinimized;
                            return {
                              ...prev,
                              isVisible: true,
                              isChatMinimized: showCanvas,
                              isArtifactMinimized: !showCanvas,
                            };
                          }

                          return {
                            ...prev,
                            isVisible: true,
                            isChatMinimized: false,
                            isArtifactMinimized: !prev.isArtifactMinimized,
                          };
                        }

                        return {
                          ...prev,
                          isVisible: !prev.isVisible,
                        };
                      })
                    }
                  >
                    {artifact.isVisible ? (
                      <PanelRightClose className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <PanelRightOpen className="h-3.5 w-3.5 shrink-0" />
                    )}
                    {!artifact.isVisible && (
                    <span className="hidden md:inline">
                      {artifactToggleLabel}
                    </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {artifactToggleLabel}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        <NotifyExplainerModal
          open={notifyExplainerOpen}
          loading={notifyLoading}
          onOpenChange={setNotifyExplainerOpen}
          onCancel={async (dontShowAgain) => {
            await persistNotifyModalDismissal(dontShowAgain);
            setNotifyExplainerOpen(false);
          }}
          onConfirm={async (dontShowAgain) => {
            await persistNotifyModalDismissal(dontShowAgain);
            setNotifyExplainerOpen(false);
            await enableNotifyForChat();
          }}
        />

        {/* Center nav.
            Logged OUT: full marketing nav (Library replaced Use Cases; rest unchanged).
            Logged IN: just the Library / Changelog / Feedback additions. */}
        {!hasMessages && !artifact.isVisible && (
          <div
            className={cn(
              "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center gap-6 text-[13px] font-normal",
              user ? "text-white" : "text-white/80",
            )}
          >
            <Link
              href="/library"
              className="hover:text-white transition-colors duration-200"
            >
              Library
            </Link>
            {/* Terminal: hover/click dropdown to the free data pages. */}
            <div className="relative group">
              <button
                type="button"
                className="hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5"
              >
                Terminal
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 hidden group-hover:block group-focus-within:block z-50">
                <div className="w-[300px] rounded-lg border border-white/[0.08] bg-[#0a0a0c] shadow-[0_12px_40px_rgba(0,0,0,0.85)] p-1.5">
                  <Link
                    href="/flow"
                    className="flex items-start gap-2.5 rounded-md px-2.5 py-2 hover:bg-white/[0.05] transition-colors"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0 animate-pulse" aria-hidden />
                    <span>
                      <span className="block text-[13px] font-medium text-white">Options Flow</span>
                      <span className="block text-[11px] text-white/80 leading-snug">
                        Every sweep, block and floor trade as it prints. Free.
                      </span>
                    </span>
                  </Link>
                  <Link
                    href="/options-screener"
                    className="flex items-start gap-2.5 rounded-md px-2.5 py-2 hover:bg-white/[0.05] transition-colors"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0 animate-pulse" aria-hidden />
                    <span>
                      <span className="block text-[13px] font-medium text-white">Options Screener</span>
                      <span className="block text-[11px] text-white/80 leading-snug">
                        Every contract, ranked by volume, premium and Greeks. Free.
                      </span>
                    </span>
                  </Link>
                  <div className="mt-1 border-t border-white/[0.08] pt-1.5 px-2.5 pb-1 flex items-center justify-between">
                    <span className="text-[10.5px] text-white/80">More datasets coming soon</span>
                    <Link href="/feedback" className="text-[10.5px] font-semibold text-emerald-400 hover:text-emerald-300">
                      Request data
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <Link
              href="/updates"
              className="relative text-orange-400 hover:text-orange-300 transition-colors duration-200"
              onClick={() => {
                try {
                  window.localStorage.setItem(
                    `${CHANGELOG_SEEN_PREFIX}${CHANGELOG_DOT_VERSION}`,
                    "1"
                  );
                  setShowChangelogDot(false);
                } catch {
                  // Ignore localStorage errors.
                }
              }}
            >
              Changelog
              {showChangelogDot && (
                <span
                  className="absolute -right-2 -top-0.5 h-1.5 w-1.5 rounded-full bg-orange-400 shadow-[0_0_8px_rgba(251,146,60,0.8)]"
                  aria-hidden
                />
              )}
            </Link>
            {!user && (
              <Link
                href="/pricing"
                className="hover:text-white transition-colors duration-200"
              >
                Pricing
              </Link>
            )}
            {!user && (
              <Link
                href="/community"
                className="hover:text-white transition-colors duration-200"
              >
                Community
              </Link>
            )}
            <Link
              href="/feedback"
              className="hover:text-white transition-colors duration-200"
            >
              Feedback
            </Link>
            {!user && (
              <Link
                href="/contact"
                className="hover:text-white transition-colors duration-200"
              >
                Contact
              </Link>
            )}
            {!user && (
              <Link
                href="/careers"
                className="hover:text-white transition-colors duration-200"
              >
                Careers
              </Link>
            )}
            {!user && (
              <Link
                href="/about"
                className="hover:text-white transition-colors duration-200"
              >
                About
              </Link>
            )}
          </div>
        )}

        {!user && (
          <>
            <div className={`${messages.length === 0 ? 'ml-auto' : ''} flex items-center gap-2.5`}>
              <Link href="/login">
                <Button
                  variant="ghost"
                  className="h-7 px-3 text-xs text-zinc-400 hover:text-white"
                >
                  Log in
                </Button>
              </Link>
              <Link href="/signup">
                <Button className="h-7 px-3.5 text-xs font-medium bg-white text-black hover:bg-zinc-200 rounded-lg">
                  Try Free →
                </Button>
              </Link>
            </div>

            <PlansModal
              isOpen={isEnterpriseModalOpen}
              onClose={() => setIsEnterpriseModalOpen(false)}
              currentPlan="free"
              stripeAccount="new"
              highlightEnterprise={true}
            />
          </>
        )}

        {/* Service Disruption Banner */}
        {/* {user && SHOW_SERVICE_DISRUPTION_BANNER && (
          <div className="mx-auto md:mr-[35vw] flex items-center gap-2 px-3 py-1 rounded-md bg-yellow-500/10 border border-yellow-500/30">
            <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
            <span className="text-xs text-yellow-500/90">
              <span className="font-bold">Service disruption due to high usage</span> - We are currently working on a fix to address this
            </span>
          </div>
        )} */}

        {/* Handbook Banner */}
        {user &&
          SHOW_HANDBOOK_BANNER &&
          !isHandbookBannerDismissed &&
          !artifact.isVisible && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 md:gap-2 md:px-3 md:py-1.5 rounded-md bg-purple-500/15 border border-purple-500/40 group max-w-[calc(100vw-2rem)] md:max-w-none">
              <BookOpen className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
              <span className="text-xs text-purple-600 dark:text-purple-400 flex-1">
                <span className="font-bold">Handbook Guide Updated </span>
                <a
                  href="https://resources.xynth.finance/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline hover:opacity-80 transition-opacity"
                >
                  Here!
                </a>
                <span className="hidden md:inline">
                  {" "}
                  - We highly recommend reading it to optimize your use with Xynth
                </span>
              </span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem("handbook_banner_dismissed", "true");
                  setIsHandbookBannerDismissed(true);
                }}
                className="flex-shrink-0 p-0.5 hover:bg-purple-500/20 rounded transition-colors"
                aria-label="Dismiss handbook banner"
              >
                <X className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
              </button>
            </div>
          )}
      </header>

     </>
   );
 }

export const ChatHeader = memo(PureChatHeader);
